import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Wand2, 
  Lightbulb, 
  Target, 
  Users, 
  Clock, 
  TrendingUp,
  BookOpen,
  Play,
  RefreshCw,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

// Sentence Templates
const sentenceTemplates = [
  {
    id: 'priorities',
    name: 'Priority Setting',
    icon: Target,
    template: "I'm working on {priority} with {team} and feeling {state} about {timeline}",
    description: 'Focus on priority management and team dynamics'
  },
  {
    id: 'decision',
    name: 'Decision Making',
    icon: TrendingUp,
    template: "I need to decide {decision} considering {factors} and I'm feeling {pressure}",
    description: 'Navigate complex decisions with clarity'
  },
  {
    id: 'learning',
    name: 'Learning & Growth',
    icon: BookOpen,
    template: "I'm learning {skill} through {method} and feeling {confidence} about {application}",
    description: 'Structure your learning journey'
  },
  {
    id: 'collaboration',
    name: 'Team Collaboration',
    icon: Users,
    template: "I'm collaborating with {team} on {project} and we're {dynamic} about {outcome}",
    description: 'Optimize team interactions and outcomes'
  }
];

// Pick List Options
const pickListOptions = {
  priority: [
    { value: 'strategic planning', label: 'Strategic Planning', category: 'strategy' },
    { value: 'daily operations', label: 'Daily Operations', category: 'operations' },
    { value: 'team development', label: 'Team Development', category: 'people' },
    { value: 'product innovation', label: 'Product Innovation', category: 'innovation' },
    { value: 'customer experience', label: 'Customer Experience', category: 'customer' },
    { value: 'process improvement', label: 'Process Improvement', category: 'process' }
  ],
  team: [
    { value: 'my direct reports', label: 'My Direct Reports', category: 'management' },
    { value: 'cross-functional team', label: 'Cross-Functional Team', category: 'collaboration' },
    { value: 'executive leadership', label: 'Executive Leadership', category: 'leadership' },
    { value: 'external partners', label: 'External Partners', category: 'external' },
    { value: 'by myself', label: 'By Myself', category: 'solo' },
    { value: 'peer colleagues', label: 'Peer Colleagues', category: 'peers' }
  ],
  state: [
    { value: 'confident', label: 'Confident', category: 'positive' },
    { value: 'uncertain', label: 'Uncertain', category: 'neutral' },
    { value: 'overwhelmed', label: 'Overwhelmed', category: 'negative' },
    { value: 'excited', label: 'Excited', category: 'positive' },
    { value: 'frustrated', label: 'Frustrated', category: 'negative' },
    { value: 'focused', label: 'Focused', category: 'positive' }
  ],
  timeline: [
    { value: 'this quarter', label: 'This Quarter', category: 'short' },
    { value: 'this month', label: 'This Month', category: 'short' },
    { value: 'this week', label: 'This Week', category: 'immediate' },
    { value: 'next year', label: 'Next Year', category: 'long' },
    { value: 'ongoing', label: 'Ongoing', category: 'continuous' },
    { value: 'deadline approaching', label: 'Deadline Approaching', category: 'urgent' }
  ],
  decision: [
    { value: 'resource allocation', label: 'Resource Allocation', category: 'resources' },
    { value: 'hiring priorities', label: 'Hiring Priorities', category: 'people' },
    { value: 'project direction', label: 'Project Direction', category: 'strategy' },
    { value: 'budget priorities', label: 'Budget Priorities', category: 'financial' },
    { value: 'technology choices', label: 'Technology Choices', category: 'technical' },
    { value: 'partnership opportunities', label: 'Partnership Opportunities', category: 'external' }
  ],
  factors: [
    { value: 'team capacity', label: 'Team Capacity', category: 'internal' },
    { value: 'market conditions', label: 'Market Conditions', category: 'external' },
    { value: 'stakeholder needs', label: 'Stakeholder Needs', category: 'stakeholder' },
    { value: 'budget constraints', label: 'Budget Constraints', category: 'financial' },
    { value: 'timeline pressure', label: 'Timeline Pressure', category: 'time' },
    { value: 'risk assessment', label: 'Risk Assessment', category: 'risk' }
  ],
  pressure: [
    { value: 'high pressure', label: 'High Pressure', category: 'stress' },
    { value: 'moderate pressure', label: 'Moderate Pressure', category: 'balanced' },
    { value: 'low pressure', label: 'Low Pressure', category: 'calm' },
    { value: 'time pressure', label: 'Time Pressure', category: 'urgent' },
    { value: 'stakeholder pressure', label: 'Stakeholder Pressure', category: 'external' },
    { value: 'self-imposed pressure', label: 'Self-Imposed Pressure', category: 'internal' }
  ],
  skill: [
    { value: 'strategic thinking', label: 'Strategic Thinking', category: 'cognitive' },
    { value: 'team leadership', label: 'Team Leadership', category: 'leadership' },
    { value: 'data analysis', label: 'Data Analysis', category: 'analytical' },
    { value: 'communication', label: 'Communication', category: 'interpersonal' },
    { value: 'project management', label: 'Project Management', category: 'operational' },
    { value: 'creative problem solving', label: 'Creative Problem Solving', category: 'creative' }
  ],
  method: [
    { value: 'formal training', label: 'Formal Training', category: 'structured' },
    { value: 'on-the-job experience', label: 'On-the-Job Experience', category: 'practical' },
    { value: 'mentorship', label: 'Mentorship', category: 'guided' },
    { value: 'self-directed learning', label: 'Self-Directed Learning', category: 'independent' },
    { value: 'peer collaboration', label: 'Peer Collaboration', category: 'collaborative' },
    { value: 'experimentation', label: 'Experimentation', category: 'experimental' }
  ],
  confidence: [
    { value: 'very confident', label: 'Very Confident', category: 'high' },
    { value: 'somewhat confident', label: 'Somewhat Confident', category: 'medium' },
    { value: 'not very confident', label: 'Not Very Confident', category: 'low' },
    { value: 'gaining confidence', label: 'Gaining Confidence', category: 'growing' },
    { value: 'losing confidence', label: 'Losing Confidence', category: 'declining' },
    { value: 'mixed feelings', label: 'Mixed Feelings', category: 'uncertain' }
  ],
  application: [
    { value: 'current role', label: 'Current Role', category: 'immediate' },
    { value: 'next role', label: 'Next Role', category: 'future' },
    { value: 'side projects', label: 'Side Projects', category: 'exploratory' },
    { value: 'team development', label: 'Team Development', category: 'collaborative' },
    { value: 'personal growth', label: 'Personal Growth', category: 'individual' },
    { value: 'career advancement', label: 'Career Advancement', category: 'professional' }
  ],
  project: [
    { value: 'product launch', label: 'Product Launch', category: 'delivery' },
    { value: 'process improvement', label: 'Process Improvement', category: 'optimization' },
    { value: 'strategic initiative', label: 'Strategic Initiative', category: 'strategy' },
    { value: 'team building', label: 'Team Building', category: 'people' },
    { value: 'customer research', label: 'Customer Research', category: 'insights' },
    { value: 'innovation project', label: 'Innovation Project', category: 'exploration' }
  ],
  dynamic: [
    { value: 'aligned', label: 'Aligned', category: 'positive' },
    { value: 'misaligned', label: 'Misaligned', category: 'negative' },
    { value: 'collaborative', label: 'Collaborative', category: 'positive' },
    { value: 'competitive', label: 'Competitive', category: 'mixed' },
    { value: 'supportive', label: 'Supportive', category: 'positive' },
    { value: 'challenging', label: 'Challenging', category: 'difficult' }
  ],
  outcome: [
    { value: 'successful delivery', label: 'Successful Delivery', category: 'achievement' },
    { value: 'learning opportunity', label: 'Learning Opportunity', category: 'growth' },
    { value: 'team cohesion', label: 'Team Cohesion', category: 'relationship' },
    { value: 'process improvement', label: 'Process Improvement', category: 'optimization' },
    { value: 'innovation breakthrough', label: 'Innovation Breakthrough', category: 'creative' },
    { value: 'stakeholder satisfaction', label: 'Stakeholder Satisfaction', category: 'external' }
  ]
};

// Recommendation Engine
const generateRecommendations = (_sentence: string, selections: Record<string, string>) => {
  const recommendations = [];

  // Priority-based recommendations
  if (selections.priority || selections.decision) {
    recommendations.push({
      type: 'framework',
      title: 'Priority Framework',
      description: 'Use the Priority Matrix to organize and rank your priorities',
      icon: Target,
      action: 'Explore Priority Matrix',
      relevance: 'high'
    });
  }
  
  // Team-based recommendations
  if (selections.team && selections.team !== 'by myself') {
    recommendations.push({
      type: 'tool',
      title: 'Team Collaboration Tools',
      description: 'Structured approaches for effective team collaboration',
      icon: Users,
      action: 'View Collaboration Tools',
      relevance: 'high'
    });
  }
  
  // Decision-making recommendations
  if (selections.decision || selections.factors) {
    recommendations.push({
      type: 'framework',
      title: 'Decision Framework',
      description: 'Systematic approach to complex decision making',
      icon: TrendingUp,
      action: 'Use Decision Framework',
      relevance: 'high'
    });
  }
  
  // Learning recommendations
  if (selections.skill || selections.method) {
    recommendations.push({
      type: 'resource',
      title: 'Learning Resources',
      description: 'Curated resources for skill development and growth',
      icon: BookOpen,
      action: 'Browse Learning Resources',
      relevance: 'high'
    });
  }
  
  // State-based recommendations
  if (selections.state === 'overwhelmed' || selections.pressure === 'high pressure') {
    recommendations.push({
      type: 'technique',
      title: 'Stress Management',
      description: 'Techniques for managing overwhelm and high-pressure situations',
      icon: Lightbulb,
      action: 'Learn Stress Management',
      relevance: 'high'
    });
  }
  
  // Timeline-based recommendations
  if (selections.timeline === 'deadline approaching' || selections.timeline === 'this week') {
    recommendations.push({
      type: 'tool',
      title: 'Time Management',
      description: 'Tools and techniques for managing tight deadlines',
      icon: Clock,
      action: 'Use Time Management Tools',
      relevance: 'high'
    });
  }
  
  // Confidence-based recommendations
  if (selections.confidence === 'not very confident' || selections.confidence === 'losing confidence') {
    recommendations.push({
      type: 'technique',
      title: 'Confidence Building',
      description: 'Strategies for building and maintaining confidence',
      icon: CheckCircle,
      action: 'Build Confidence',
      relevance: 'high'
    });
  }
  
  return recommendations;
};

export const SentenceBuilder: React.FC = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(sentenceTemplates[0]);
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [currentStep, setCurrentStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  // Extract variables from template
  const templateVariables = useMemo(() => {
    const matches = selectedTemplate.template.match(/\{([^}]+)\}/g);
    return matches ? matches.map(match => match.slice(1, -1)) : [];
  }, [selectedTemplate]);

  // Generate completed sentence
  const completedSentence = useMemo(() => {
    let sentence = selectedTemplate.template;
    templateVariables.forEach(variable => {
      const selection = selections[variable];
      if (selection) {
        const option = pickListOptions[variable as keyof typeof pickListOptions]?.find(opt => opt.value === selection);
        sentence = sentence.replace(`{${variable}}`, option?.label || selection);
      }
    });
    return sentence;
  }, [selectedTemplate.template, selections, templateVariables]);

  // Generate recommendations
  const recommendations = useMemo(() => {
    return generateRecommendations(completedSentence, selections);
  }, [completedSentence, selections]);

  const handleSelection = (variable: string, value: string) => {
    setSelections(prev => ({ ...prev, [variable]: value }));
    if (currentStep < templateVariables.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setIsComplete(true);
    }
  };

  const resetBuilder = () => {
    setSelections({});
    setCurrentStep(0);
    setIsComplete(false);
  };

  const getCurrentVariable = () => {
    return templateVariables[currentStep];
  };

  const getCurrentOptions = () => {
    const variable = getCurrentVariable();
    return pickListOptions[variable as keyof typeof pickListOptions] || [];
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <Link to="/elevation/lab" className="inline-flex items-center gap-2 text-[var(--mustard)] hover:text-[var(--mustard)] mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to The Lab
        </Link>
        <div className="flex items-center gap-3 mb-4">
          <div className="px-4 py-2 font-bold text-xl bg-[var(--mustard-dim)] text-[var(--mustard)]">
            The Lab
          </div>
          <Wand2 className="h-10 w-10 text-[var(--mustard)]" />
          <div>
            <h1 className="text-3xl font-bold text-charcoal-900">Sentence Builder</h1>
            <p className="text-charcoal-600">Build your context, get personalized recommendations</p>
          </div>
        </div>
        <p className="text-lg text-charcoal-700">
          Inspired by Beats' "Beats the Sentence" - create a structured sentence about your situation 
          and get personalized Peak Priorities recommendations based on your context.
        </p>
      </div>

      {/* Template Selection */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
        <h2 className="text-xl font-semibold text-charcoal-900 mb-4">Choose Your Context</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sentenceTemplates.map((template) => {
            const Icon = template.icon;
            return (
              <button
                key={template.id}
                onClick={() => {
                  setSelectedTemplate(template);
                  resetBuilder();
                }}
                className={`p-4  border-2 text-left transition-all ${
                  selectedTemplate.id === template.id
                    ? 'border-[var(--mustard)] bg-[var(--mustard-dim)]'
                    : 'border-[var(--rule)] hover:border-[var(--rule)]'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Icon className="h-6 w-6 text-[var(--mustard)]" />
                  <h3 className="font-semibold text-charcoal-900">{template.name}</h3>
                </div>
                <p className="text-sm text-charcoal-600 mb-2">{template.description}</p>
                <p className="text-xs text-charcoal-500 font-mono bg-[var(--hover-tint)] p-2 rounded">
                  "{template.template}"
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Sentence Building */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-charcoal-900">Build Your Sentence</h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-charcoal-600">
              Step {currentStep + 1} of {templateVariables.length}
            </span>
            <div className="w-32 bg-[var(--hover-tint)] rounded-full h-2">
              <div 
                className="bg-[var(--mustard)] h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / templateVariables.length) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Current Sentence Preview */}
        <div className="bg-charcoal-50 p-4 mb-6">
          <h3 className="text-sm font-medium text-charcoal-700 mb-2">Your Sentence:</h3>
          <p className="text-lg text-charcoal-900 font-medium">
            {completedSentence}
          </p>
        </div>

        {/* Current Selection */}
        {!isComplete && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-charcoal-900">
              Choose: {getCurrentVariable().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {getCurrentOptions().map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSelection(getCurrentVariable(), option.value)}
                  className="p-4 border-2 border-[var(--rule)] hover:border-[var(--mustard)] hover:bg-[var(--mustard-dim)] text-left transition-all"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-charcoal-900">{option.label}</span>
                    <ArrowRight className="h-4 w-4 text-charcoal-400" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Reset Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={resetBuilder}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-charcoal-600 hover:text-charcoal-800 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Start Over
          </button>
        </div>
      </div>

      {/* Recommendations */}
      {isComplete && (
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
          <div className="flex items-center gap-3 mb-6">
            <Lightbulb className="h-6 w-6 text-[var(--mustard)]" />
            <h2 className="text-xl font-semibold text-charcoal-900">Personalized Recommendations</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recommendations.map((rec, index) => {
              const Icon = rec.icon;
              return (
                <div
                  key={index}
                  className="p-4 border-2 border-[var(--rule)] hover:border-[var(--mustard)] hover:shadow-md transition-all"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-[var(--mustard-dim)]">
                      <Icon className="h-5 w-5 text-[var(--mustard)]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-charcoal-900">{rec.title}</h3>
                        <span className="px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs rounded-full">
                          {rec.type}
                        </span>
                      </div>
                      <p className="text-sm text-charcoal-600 mb-3">{rec.description}</p>
                      <button className="inline-flex items-center gap-2 text-sm text-[var(--mustard)] hover:text-[var(--mustard)] font-medium">
                        <Play className="h-4 w-4" />
                        {rec.action}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
